import React, {
	useEffect,
	useMemo,
	useState,
} from 'react';

import Collection from 'Views/Components/Collection/Collection';
import { ICollectionHeaderProps } from 'Views/Components/Collection/CollectionHeaders';
import { useOnMount } from 'Hooks/useMounted';

import { Conditions } from 'Util/QueryHelpers';
import alertToast from 'Util/ToastifyUtils';
import { getStatus } from 'Util/UserUtils';
import { startCase } from 'lodash';
import Chip from 'Views/Components/Chip/Chip';
import { useAutoAnimate } from '@formkit/auto-animate/react';
import moment from 'moment';
import { hasAnyRole } from 'Util/SecurityUtils';
import { getAllStudents } from 'Queries/GraphQlQueries/User/UserEntityQueries';
import { IUserEntityAttributes, UserEntity } from 'Models/Entities';
import { observable } from 'mobx';
import { useHistory } from 'react-router';
import { toLower } from 'lodash';
import useStore from 'Hooks/useStore';

const PAGE_SIZE = 10;

interface IExtendedUserValues {
	lastAccessed: string | null;
	emailConfirmed: boolean;
	lockoutEnd: Date | null;
}

class ExtendedUserEntity extends UserEntity {
	@observable
	public lastAccessed: Date | null = null;

	@observable
	public emailConfirmed: boolean;

	@observable
	public lockoutEnd: Date | null = null

	constructor(attributes?: Partial<IUserEntityAttributes & IExtendedUserValues>) {
		super(attributes);

		if (attributes) {
			if (attributes.lastAccessed) {
				this.lastAccessed = new Date(attributes.lastAccessed);
			}

			if (attributes.emailConfirmed !== undefined) {
				this.emailConfirmed = attributes.emailConfirmed;
			}

			if (attributes.lockoutEnd) {
				this.lockoutEnd = new Date(attributes.lockoutEnd);
			}
		}
	}
}

const headers: Array<ICollectionHeaderProps<ExtendedUserEntity>> = [
	{
		name: 'studentId',
		displayName: window.lexicon.studentID,
		transformItem: user => user.studentId ?? '-',
	},
	{
		name: 'lastName',
		displayName: 'Last name',
	},
	{
		name: 'firstName',
		displayName: 'First name',
	},
	{
		name: 'email',
		displayName: 'Email',
	},
	{
		name: 'programCode',
		displayName: `${window.lexicon.program} ${toLower(window.lexicon.code)}`,
		transformItem: user => user.studyPlans
			.find(plan => plan.active)?.programVersion.program.code ?? '-',
	},
	{
		name: 'programName',
		displayName: `${window.lexicon.program} ${toLower(window.lexicon.title)}`,
		transformItem: user => user.studyPlans
			.find(plan => plan.active)?.programVersion.program.displayName ?? '-',
	},
	{
		name: 'lastAccessed',
		displayName: 'Last accessed',
		transformItem: user => user.lastAccessed
			? moment(user.lastAccessed).format('DD/MM/YYYY')
			: '-',
	},
];

interface IStudentList {
	searchTerm?: string;
	studentFilters: Conditions,
	studyPlanFilters: Conditions,
}

const StudentList: React.FC<IStudentList> = ({
	searchTerm = '',
	studentFilters,
	studyPlanFilters,
}) => {
	const [animationRef] = useAutoAnimate();
	const history = useHistory();
	const store = useStore();

	const [page, setPage] = useState(0);
	const [data, setData] = useState<ExtendedUserEntity[]>([]);
	const [totalRecordCount, setTotalRecordCount] = useState<number>(0);

	const resetPage = () => setPage(0);

	useEffect(
		() => resetPage(),
		[searchTerm, studentFilters, studyPlanFilters],
	);

	useOnMount(
		(isMounted, abortSignals) => {
			const searchQuery = `%${searchTerm}%`;

			const hasFilter = studyPlanFilters.length > 0
				? [[
					{
						path: 'studyPlans',
						conditions: [
							...studyPlanFilters,
						],
					},
				]] : [];

			return getAllStudents(
				abortSignals,
				[ // AND
					[ // OR
						{
							path: 'email',
							comparison: 'Like',
							value: [searchQuery],
							case: 'INVARIANT_CULTURE_IGNORE_CASE',
						},
						{
							path: 'firstName',
							comparison: 'Like',
							value: [searchQuery],
							case: 'INVARIANT_CULTURE_IGNORE_CASE',
						},
						{
							path: 'lastName',
							comparison: 'Like',
							value: [searchQuery],
							case: 'INVARIANT_CULTURE_IGNORE_CASE',
						},
						{
							path: 'studentId',
							comparison: 'Like',
							value: [searchQuery],
							case: 'INVARIANT_CULTURE_IGNORE_CASE',
						},
					],
					...studentFilters,
				],
				hasFilter,
				[
					{
						path: 'lockoutEnd',
						descending: true,
					},
					{
						path: 'emailConfirmed',
						descending: true,
					},
					{
						path: 'lastName',
						descending: false,
					},
				],
				page * PAGE_SIZE,
				PAGE_SIZE,
			).then(
				response => {
					if (isMounted()) {
						setData(
							response.data.userEntitys
								.map(userEntityAttr => new ExtendedUserEntity(userEntityAttr)),
						);
						setTotalRecordCount(response.data.countUserEntitys.number);
					}
				},
			).catch(err => {
				alertToast('Failed to retrieve students', 'error');
			});
		},
		[page, searchTerm, studentFilters, studyPlanFilters],
	);

	const customisedHeaders = useMemo(
		() => ([
			...headers,
			...(
				hasAnyRole(store, ['Admin', 'InstitutionAdmin', 'CurriculumSupport', 'StudentAdvisor'])
					? [{
						name: 'status',
						displayName: 'Status',
						transformItem: (extendedUser: ExtendedUserEntity) => {
							const lowerCaseStatus = getStatus(
								extendedUser.lockoutEnd,
								extendedUser.emailConfirmed,
							).toLowerCase();
							const label = startCase(lowerCaseStatus);
							return (
								<Chip chipLabel={label} className={`user-status ${lowerCaseStatus}`} />
							);
						},
					}]
					: []
			),
		]),
		[],
	);

	return (
		<Collection<ExtendedUserEntity>
			className="staff-list"
			ariaLabel="Students list"
			headers={customisedHeaders}
			collection={data}
			onPageChange={newPageNumber => setPage(newPageNumber)}
			pageNo={page}
			perPage={PAGE_SIZE}
			totalRecords={totalRecordCount}
			idColumn="id"
			onRowClick={extendedUserEntity => {
				// go to view details page
				history.push(`/users/student/${extendedUserEntity.id}`);
			}}
			noDataMessage="No students found"
			animationRef={animationRef}
			actionsMore={[
				{
					icon: 'edit',
					iconPos: 'icon-left',
					label: 'Edit',
					onEntityClick: (event, extendedUserEntity) => {
						event.event.stopPropagation();
						// go to edit page
						history.push(
							`/users/student/edit/${extendedUserEntity.id}`,
						);
					},
				},
			]}
		/>
	);
};

export default StudentList;
